import React, { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Select, Table, Tag, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Portfolio } from '../../hooks/usePortfolios';
import { boardError, BrokerOrder, BrokerOrders, priceBoardApi } from '../../services/priceBoardApi';
import { formatInt } from '../../utils/numberFormat';
import { boardPrice, fromApiPrice } from './boardMath';

interface Props {
    portfolios: Portfolio[];
    compact?: boolean;
}

// The broker takes a moment to report a cancel, so re-read a few times after one.
const REFRESH_AFTER_CANCEL_MS = [1500, 4000, 8000];

const OWNER_NAME: Record<BrokerOrder['owner'], string> = {
    BOARD: 'Board',
    GRID: 'Grid',
    PAIR: 'Pair',
    SMART_BETA: 'Smart Beta',
    UNKNOWN: 'Not Fortuna',
};
const isBot = (o: BrokerOrder) => o.owner === 'GRID' || o.owner === 'PAIR' || o.owner === 'SMART_BETA';

export const ownerLabel = (o: BrokerOrder): string =>
    isBot(o) ? `${OWNER_NAME[o.owner]} · ${o.ownerRunning ? 'running' : 'stopped'}` : OWNER_NAME[o.owner];

const ownerColor = (o: BrokerOrder): string =>
    o.owner === 'UNKNOWN' ? 'default' : o.owner === 'BOARD' ? 'blue' : o.ownerRunning ? 'green' : 'orange';

const ownerSentence = (o: BrokerOrder): string => {
    if (isBot(o)) return `This order belongs to a ${o.ownerRunning ? 'running' : 'stopped'} ${OWNER_NAME[o.owner].toLowerCase()}.`;
    if (o.owner === 'BOARD') return 'This order was placed from the price board.';
    return 'This order was not placed through Fortuna.';
};

const priceText = (o: BrokerOrder) => (o.price == null ? 'Market' : boardPrice(o.symbol, fromApiPrice(o.symbol, o.price)));

/** Orders still live at the broker for one account; cancel any whose exchange id is known. */
const BrokerOrdersPanel: React.FC<Props> = ({ portfolios, compact }) => {
    const { message, modal } = App.useApp();
    const [accountId, setAccountId] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (!portfolios.some((p) => p.id === accountId)) setAccountId(portfolios[0]?.id);
    }, [portfolios, accountId]);

    const [data, setData] = useState<BrokerOrders | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const current = useRef<string | undefined>(undefined);
    const timers = useRef<number[]>([]);

    const load = useCallback(async (id: string | undefined) => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await priceBoardApi.brokerOrders(id);
            if (current.current !== id) return; // account switched meanwhile
            setData(res);
            setError(null);
        } catch (e) {
            if (current.current === id) setError(boardError(e, 'Could not read broker orders'));
        } finally {
            if (current.current === id) setLoading(false);
        }
    }, []);

    useEffect(() => {
        current.current = accountId;
        setData(null);
        setError(null);
        void load(accountId);
    }, [accountId, load]);
    useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

    const account = portfolios.find((p) => p.id === accountId);
    const terminal = (data?.route ?? account?.brokerMode) === 'CLIENT';

    const cancel = (o: BrokerOrder) => {
        if (!accountId || !o.exchangeOrderId) return;
        const exchangeOrderId = o.exchangeOrderId;
        modal.confirm({
            title: `Cancel ${o.side === 'BUY' ? 'buy' : 'sell'} ${formatInt(o.quantity)} ${o.symbol} at ${priceText(o)}?`,
            content: (
                <div style={{ fontSize: 12 }}>
                    <div>{ownerSentence(o)}</div>
                    <div>Exchange order {exchangeOrderId}</div>
                    {terminal && <div>Sent to your desktop terminal: it must be running for the cancel to reach the broker.</div>}
                </div>
            ),
            okText: 'Cancel order',
            okButtonProps: { danger: true },
            cancelText: 'Keep',
            onOk: async () => {
                try {
                    await priceBoardApi.cancelBrokerOrder(accountId, exchangeOrderId);
                    message.success('Cancel requested');
                    timers.current.forEach((t) => window.clearTimeout(t));
                    timers.current = REFRESH_AFTER_CANCEL_MS.map((ms) => window.setTimeout(() => void load(accountId), ms));
                } catch (e) {
                    message.error(boardError(e, 'Cancel not sent'));
                }
            },
        });
    };

    const cancelButton = (o: BrokerOrder) => {
        const btn = (
            <Button size="small" danger type={compact ? 'default' : 'text'} disabled={!o.cancellable} onClick={() => cancel(o)}>
                Cancel
            </Button>
        );
        // A disabled button swallows hover, so the tooltip wraps a span.
        return !o.cancellable && o.reason ? (
            <Tooltip title={o.reason}>
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>{btn}</span>
            </Tooltip>
        ) : (
            btn
        );
    };
    const side = (s: BrokerOrder['side']) => <span style={{ color: s === 'BUY' ? '#0ECB81' : '#F6465D', fontWeight: 600 }}>{s === 'BUY' ? 'Buy' : 'Sell'}</span>;
    const owner = (o: BrokerOrder) => (
        <Tag color={ownerColor(o)} style={{ marginInlineEnd: 0 }}>
            {ownerLabel(o)}
        </Tag>
    );
    const rowKey = (o: BrokerOrder) => o.exchangeOrderId ?? o.clientOrderId ?? `${o.symbol}-${o.side}-${o.price}-${o.quantity}`;

    const columns: ColumnsType<BrokerOrder> = [
        { title: 'Symbol', dataIndex: 'symbol', width: 100, render: (s: string) => <strong>{s}</strong> },
        { title: 'Side', dataIndex: 'side', width: 56, render: side },
        { title: 'Price', key: 'price', width: 90, align: 'right', render: (_: unknown, o) => priceText(o) },
        { title: 'Filled / Qty', key: 'qty', width: 110, align: 'right', render: (_: unknown, o) => `${formatInt(o.filledQuantity)} / ${formatInt(o.quantity)}` },
        { title: 'Exchange ID', dataIndex: 'exchangeOrderId', width: 130, ellipsis: true, render: (v: string | null) => v ?? '—' },
        { title: 'Status', dataIndex: 'status', width: 110, ellipsis: true },
        { title: 'Owner', key: 'owner', width: 120, render: (_: unknown, o) => owner(o) },
        { title: '', key: 'cancel', width: 80, render: (_: unknown, o) => cancelButton(o) },
    ];

    const orders = data?.orders ?? [];
    const empty = loading && !data ? 'Loading…' : error ?? 'No live orders at the broker';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Select
                    size="small"
                    value={accountId}
                    onChange={setAccountId}
                    placeholder="No account"
                    style={{ flex: compact ? 1 : undefined, width: compact ? undefined : 240, minWidth: 0 }}
                    options={portfolios.map((p) => ({ value: p.id, label: `${p.portfolioName}${p.brokerMode === 'CLIENT' ? ' · terminal' : ''}` }))}
                />
                <Button size="small" icon={<ReloadOutlined />} loading={loading} disabled={!accountId} onClick={() => void load(accountId)} aria-label="Refresh" />
            </div>
            {data && !data.brokerListed && (
                <div style={{ color: '#848E9C', fontSize: 11 }}>Broker orders not readable for this account — showing orders Fortuna recorded today</div>
            )}
            {error && data && <div style={{ color: '#F6465D', fontSize: 11 }}>{error}</div>}
            {compact ? (
                orders.length ? (
                    <div data-testid="broker-order-cards">
                        {orders.map((o, i) => (
                            <div key={rowKey(o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: i ? '1px solid #2B3139' : undefined }}>
                                <div style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                                    <div>
                                        {side(o.side)} <strong>{o.symbol}</strong> {formatInt(o.filledQuantity)}/{formatInt(o.quantity)} @ {priceText(o)}
                                    </div>
                                    <div style={{ color: '#848E9C', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {o.exchangeOrderId ?? 'no exchange id'} · {o.status}
                                    </div>
                                </div>
                                {owner(o)}
                                {cancelButton(o)}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ color: '#848E9C', padding: '8px 0' }}>{empty}</div>
                )
            ) : (
                <Table<BrokerOrder>
                    size="small"
                    rowKey={rowKey}
                    loading={loading && !data}
                    dataSource={orders}
                    columns={columns}
                    pagination={false}
                    tableLayout="fixed"
                    className="price-board-table"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: empty }}
                />
            )}
        </div>
    );
};

export default BrokerOrdersPanel;
